<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="close"
  >
    <div class="bg-card rounded-lg border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
      <div class="p-6 border-b">
        <h2 class="text-xl font-semibold">Edit Position</h2>
        <p class="text-sm text-muted-foreground mt-1">Update position details</p>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label for="edit_symbol" class="text-sm font-medium">Symbol</label>
            <SymbolSelector
              id="edit_symbol"
              v-model="formData.symbol"
              placeholder="Search for a symbol..."
            />
          </div>

          <div class="space-y-2">
            <label for="edit_stock_qty" class="text-sm font-medium">Stock Qty</label>
            <input
              id="edit_stock_qty"
              v-model.number="formData.stock_qty"
              type="number"
              min="1"
              placeholder="100"
              class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              required
            />
          </div>

          <div class="space-y-2">
            <label for="edit_purchase_price" class="text-sm font-medium">Purchase Price</label>
            <input
              id="edit_purchase_price"
              v-model.number="formData.purchase_price"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="183.16"
              class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              required
            />
          </div>

          <div class="space-y-2">
            <label for="edit_premium_mark" class="text-sm font-medium">
              Premium Mark <span class="text-muted-foreground text-xs">(optional)</span>
            </label>
            <input
              id="edit_premium_mark"
              v-model.number="formData.premium_mark"
              type="number"
              step="0.01"
              min="0"
              placeholder="4.20"
              class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div class="space-y-2">
            <label for="edit_expire_date" class="text-sm font-medium">
              Expire Date <span class="text-muted-foreground text-xs">(optional)</span>
            </label>
            <DatePicker
              id="edit_expire_date"
              v-model="formData.expire_date"
              placeholder="Select date..."
            />
          </div>

          <div class="space-y-2 md:col-span-2">
            <label for="edit_notes" class="text-sm font-medium">Notes</label>
            <textarea
              id="edit_notes"
              v-model="formData.notes"
              rows="3"
              placeholder="Optional notes about the position..."
              class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        <!-- Live Calculations Display -->
        <div class="flex items-center space-x-6 text-sm pt-2 border-t">
          <div class="flex items-center space-x-2">
            <span class="text-muted-foreground">Cash:</span>
            <span class="font-medium text-green-600"
              >${{
                liveCash.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}</span
            >
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-muted-foreground">Premium:</span>
            <span class="font-medium text-purple-600"
              >${{
                livePremium.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}</span
            >
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            @click="close"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isUpdating"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
          >
            <svg
              v-if="isUpdating"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isUpdating ? "Updating..." : "Update Position" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { apiClient, type Position, type PositionCreate } from '@/lib/api'
import SymbolSelector from '@/components/SymbolSelector.vue'
import DatePicker from '@/components/DatePicker.vue'

const props = defineProps<{
  isOpen: boolean
  position: Position | null
}>()

const emit = defineEmits<{
  close: []
  updated: []
}>()

const isUpdating = ref(false)
const formData = reactive<PositionCreate>({
  symbol: '',
  stock_qty: 0,
  purchase_price: 0,
  premium_mark: null,
  expire_date: null,
  notes: null
})

// Live calculations
const liveCash = computed(() => {
  if (formData.stock_qty && formData.purchase_price) {
    return formData.stock_qty * formData.purchase_price
  }
  return 0
})

const livePremium = computed(() => {
  if (formData.stock_qty && formData.premium_mark) {
    return formData.stock_qty * formData.premium_mark
  }
  return 0
})

// Watch for position changes and populate form
watch(() => props.position, (newPosition) => {
  if (newPosition) {
    formData.symbol = newPosition.symbol
    formData.stock_qty = newPosition.stock_qty
    formData.purchase_price = newPosition.purchase_price
    formData.premium_mark = newPosition.premium_mark ?? null
    formData.expire_date = newPosition.expire_date ?? null
    formData.notes = newPosition.notes ?? null
  }
}, { immediate: true })

const close = () => {
  emit('close')
}

const handleSubmit = async () => {
  if (!props.position) return

  isUpdating.value = true
  try {
    await apiClient.updatePosition(props.position.id, formData)
    emit('updated')
    close()
  } catch (error) {
    console.error('Failed to update position:', error)
    alert('Failed to update position. Please try again.')
  } finally {
    isUpdating.value = false
  }
}
</script>

